import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import propertyImportsService from '../../service/api/PropertyImportsService';
import FormErrorMessage from '../_shared/FormErrorMessage';
import FormErrorMessageScroller from '../_shared/FormErrorMessageScroller';
import { useTranslation } from 'react-i18next';
import { PropertyImport } from '../../types/PropertyImport';
import { FileUpload } from 'primereact/fileupload';
import enumService from '../../service/EnumService';

interface FormValuesProps extends PropertyImport {
  file?: any;
}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
  creationDate: null,
  creation: true,
  file: null,
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  propertyImport: PropertyImport;

  readOnly?: boolean;
  showToast: (summary: string, detail: string, severity: string) => void;
};

function PropertyImportDialog({
  dialogVisible,
  closeAddEditModal,
  propertyImport,

  readOnly,
  showToast,
}: Props) {
  const [initFormValues, setInitFormValues] = useState(INIT_FORM_STATE);

  const { t } = useTranslation();
  const fileUploader = useRef(null);

  const hideDialog = () => {
    formik.resetForm({ values: INIT_FORM_STATE });
    closeAddEditModal();
  };

  const importTypes = useMemo(() => enumService.getPropertyImportTypes(t), [t]);

  const editMode = !!propertyImport;
  useEffect(() => {
    if (dialogVisible && editMode) {
      setInitFormValues({
        id: propertyImport.id,
        name: propertyImport.name,
        creationDate: propertyImport.creationDate,
        creation: propertyImport.creation,
      });
    } else {
      setInitFormValues(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const formik = useFormik({
    initialValues: initFormValues,
    validate: (data: FormValuesProps) => {
      const errors: FormikErrors<FormValuesProps> = {};

      if (!data.name?.trim()) {
        errors.name = t('propertyImports.error_name_required');
      }

      if (!data.file) {
        errors.file = t('propertyImports.error_file_required');
      }

      return errors;
    },
    onSubmit: async (formData: FormValuesProps, helpers: FormikHelpers<FormValuesProps>) => {
      try {
        await propertyImportsService.create(formData, formData.file);
        fileUploader.current.clear();
        hideDialog();
        showToast(t('common.dataImportSuccess'), '', 'success');
      } catch (error: any) {
        if (error.response.status === 409) {
          const field = error.response.data.field;
          helpers.setFieldError(field, t(`propertyImports.error_${field}_already_taken`));
        } else {
          showToast(t('common.dataImportError'), t('propertyImports.dataImportErrorDetails'), 'error');
        }
      }
      helpers.setSubmitting(false);
    },
    enableReinitialize: true,
  });

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        disabled={readOnly ? true : undefined}
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={formik.submitForm}
      />
    </>
  );

  const onUploadFile = (data: any) => {
    const [f] = data.files;
    formik.setFieldValue('file', f, true);
    if (fileUploader.current) {
      fileUploader.current.clear();
    }
  };

  return (
    <FormErrorMessageScroller formikInstance={formik}>
      <Dialog
        visible={dialogVisible}
        header={t('propertyImports.property_import_details')}
        modal
        className="p-fluid"
        footer={DialogFooter}
        onHide={hideDialog}
        breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
        style={{ width: '40vw' }}
      >
        <form>
          <div className="col-10">
            <div className="field">
              <label htmlFor="creation">{t('propertyImports.type')}</label>
              <Dropdown
                id="creation"
                value={formik.values.creation}
                onChange={formik.handleChange}
                options={importTypes}
                placeholder={t('common.select')}
              />
              {formik.values.creation === false && (
                <small id={`error-msg-creation`} className={'p-error'}>
                  {t('propertyImports.deletion_warning')}
                </small>
              )}
            </div>

            <div className="field">
              <label htmlFor="name">{t('propertyImports.name')}</label>
              <InputText
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                disabled={readOnly ? true : undefined}
                className={formik.touched.name && formik.errors.name && 'p-invalid'}
              />
              <FormErrorMessage fieldName="name" formikInstance={formik} />
            </div>

            <div className="field grid">
              <div className="field col-9">
                <label htmlFor="file">{t('propertyImports.file')}</label>
                <InputText
                  id="file"
                  value={formik.values.file?.name}
                  onChange={formik.handleChange}
                  disabled={true}
                  className={formik.touched.file && formik.errors.file && 'p-invalid'}
                />
                <FormErrorMessage fieldName="file" formikInstance={formik} />
              </div>
              <div className="field col-3 align-content-end">
                <FileUpload
                  ref={fileUploader}
                  mode="basic"
                  accept=".xlsx"
                  maxFileSize={1000000}
                  className="ml-2 flex-shrink-0"
                  customUpload
                  auto
                  uploadHandler={onUploadFile}
                  chooseLabel={t('common.select')}
                />
              </div>
            </div>
          </div>
        </form>
      </Dialog>
    </FormErrorMessageScroller>
  );
}

export default PropertyImportDialog;
